import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import deepResearchStore from "Stores/deepResearchData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import { ReactComponent as DeleteIcon } from "Assets/svg/deleteIcon.svg";
import SellerUrlPromptPopup from "./SellerUrlPromptPopup";

const SellerProfileCardLoader = ({ length = 4 }) =>
  Array.from({ length }, (_, i) => (
    <div className="sellerProfileCard sellerProfileCardLoader" key={i}>
      <div className="sellerProfileCardName linear-background" />
      <div className="sellerProfileCardMeta linear-background" />
    </div>
  ));

const formatDate = (raw) => {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch (e) {
    return "";
  }
};

const profileDisplayName = (p) => {
  if (!p) return "Untitled";
  return p.name || p.company_name || p.product_name || "Untitled profile";
};

const profileSubtitle = (p) => {
  const bits = [];
  if (p.product_name && p.product_name !== p.company_name) bits.push(p.product_name);
  if (p.goal_of_outreach) bits.push(p.goal_of_outreach);
  return bits.join(" • ");
};

const SellerProfiles = observer(({ history }) => {
  const [showUrlPopup, setShowUrlPopup] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    deepResearchStore
      .fetchSellerProfiles({ force: true })
      .finally(() => setLoading(false));
  }, []);

  const profiles = deepResearchStore.sellerProfiles || [];

  const openCreate = () => setShowUrlPopup(true);
  const closeUrlPopup = () => setShowUrlPopup(false);

  const handleProceedFromUrl = (draft) => {
    setShowUrlPopup(false);
    history.push({
      pathname: "/settings/seller-profiles/new",
      state: { draft }
    });
  };

  const openProfile = (profile) => {
    if (!profile?.id) return;
    history.push(`/settings/seller-profiles/${profile.id}`);
  };

  const handleDelete = (e, profile) => {
    if (e) e.stopPropagation();
    confirmationPopupState.setPopupValues({
      content: `Delete "${profileDisplayName(
        profile
      )}"? Sequences using this profile will need a different one.`,
      actionBtnText: "Delete",
      cancelBtnText: "Cancel",
      loadingMsg: "Please wait...",
      callback: () => {
        deepResearchStore.deleteSellerProfile(profile.id).then((ok) => {
          confirmationPopupState.setShowPopup(false);
          toasterState.setToastMsg(
            ok ? "Seller profile deleted" : "Could not delete seller profile",
            ok ? "success" : "failure"
          );
        });
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  return (
    <section className="sellerProfilesWrapper">
      <div className="sellerProfilesCont">
        <div className="headerCont">
          <div className="headerTitle">Seller Profiles</div>
          <p className="headerSubtitle">
            Profiles describe what you sell, who you sell to, and what good outreach
            looks like. Deep Research uses these to personalize every message.
            Profiles are shared across your team.
          </p>
        </div>
        <div className="sellerProfilesContentBody">
          <div className="sellerProfileCardList">
            {loading ? (
              <SellerProfileCardLoader length={4} />
            ) : (
              <>
                {profiles.map((p) => (
                  <div
                    className="sellerProfileCard"
                    key={p.id}
                    onClick={() => openProfile(p)}
                  >
                    <div className="sellerProfileCardBody">
                      <div className="sellerProfileCardName">
                        {profileDisplayName(p)}
                      </div>
                      {profileSubtitle(p) && (
                        <div className="sellerProfileCardSubtitle">
                          {profileSubtitle(p)}
                        </div>
                      )}
                      <div className="sellerProfileCardMeta">
                        {p.updatedAt
                          ? `Updated ${formatDate(p.updatedAt)}`
                          : p.createdAt
                          ? `Created ${formatDate(p.createdAt)}`
                          : "—"}
                      </div>
                    </div>
                    <div className="sellerProfileCardActions">
                      <span
                        className="iconBackground deleteIcon"
                        onClick={(e) => handleDelete(e, p)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </span>
                    </div>
                  </div>
                ))}
                <div
                  className="sellerProfileCard createNewSellerProfileCard"
                  onClick={openCreate}
                >
                  <div className="sellerProfileCardName">
                    Create new seller profile
                  </div>
                  <span className="iconBackground">
                    <i className="material-icons">add</i>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {showUrlPopup && (
        <SellerUrlPromptPopup
          onClose={closeUrlPopup}
          onProceed={handleProceedFromUrl}
        />
      )}
    </section>
  );
});

export default withRouter(SellerProfiles);
